/// <reference types="vite/client" />
// The triple-slash reference above (not `packages/ui/src/vite-env.d.ts`) is what
// makes the `*.css` ambient module declaration available here — this file is
// consumed by non-Vite tsconfigs too (e.g. `apps/custom-made`, whose own tsconfig
// doesn't include @susu/ui's vite-env.d.ts), and without it `tsc` fails with
// "Cannot find module '...css' or its corresponding type declarations." on the
// dynamic `import('intl-tel-input/build/css/intlTelInput.css')` below.
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

interface IntlTelInputOptions {
  initialCountry?: string
  preferredCountries?: string[]
  separateDialCode?: boolean
  autoPlaceholder?: 'off' | 'polite' | 'aggressive'
  nationalMode?: boolean
}

interface IntlTelInputInstance {
  isValidNumber: () => boolean
  getNumber: () => string
  setNumber: (number: string) => void
  destroy: () => void
}

export function useIntlTelInput(
  inputRef: React.RefObject<HTMLInputElement | null>,
  options: IntlTelInputOptions = {},
) {
  const [isLoaded, setIsLoaded] = useState(false)
  const instanceRef = useRef<IntlTelInputInstance | null>(null)
  const optionsRef = useRef(options)

  useEffect(() => {
    optionsRef.current = options
  }, [options])

  // biome-ignore lint/correctness/useExhaustiveDependencies: doesn't need dependencies
  useEffect(() => {
    if (typeof window === 'undefined' || !inputRef.current) return

    // Don't re-initialize if already initialized
    if (instanceRef.current) return

    let isMounted = true

    const initializeIntlTelInput = async () => {
      try {
        // Dynamically import the library
        const intlTelInputModule = await import('intl-tel-input')
        const intlTelInput = intlTelInputModule.default || intlTelInputModule

        // Import CSS dynamically
        await import('intl-tel-input/build/css/intlTelInput.css')

        if (!isMounted || !inputRef.current || instanceRef.current) return

        const opts = optionsRef.current

        // Initialize intl-tel-input
        const instance = intlTelInput(inputRef.current, {
          initialCountry: opts.initialCountry || 'us',
          preferredCountries: opts.preferredCountries || [
            'us',
            'gb',
            'nl',
            'be',
            'de',
            'fr',
            'it',
            'ca',
          ],
          utilsScript: 'https://cdn.jsdelivr.net/npm/intl-tel-input@17.0.21/build/js/utils.js',
          separateDialCode: opts.separateDialCode ?? true,
          autoPlaceholder: opts.autoPlaceholder ?? 'polite',
          nationalMode: opts.nationalMode ?? true,
        }) as IntlTelInputInstance & {promise?: Promise<void>}

        instanceRef.current = instance

        // Wait for utils.js to finish loading before marking as ready.
        // isValidNumber() only works correctly after the utils script is loaded.
        if (instance.promise) {
          await instance.promise
        }

        if (!isMounted) return
        setIsLoaded(true)
      } catch (error) {
        console.error('Failed to initialize intl-tel-input:', error)
      }
    }

    initializeIntlTelInput()

    return () => {
      isMounted = false
      if (instanceRef.current) {
        try {
          instanceRef.current.destroy()
        } catch {
          // Ignore cleanup errors
        }
        instanceRef.current = null
        setIsLoaded(false)
      }
    }
    // Only run once when the component mounts
  }, [])

  const validateNumber = useCallback(() => {
    if (instanceRef.current && isLoaded) {
      return instanceRef.current.isValidNumber()
    }
    return false
  }, [isLoaded])

  const getNumber = useCallback(() => {
    if (instanceRef.current && isLoaded) {
      try {
        return instanceRef.current.getNumber()
      } catch {
        return ''
      }
    }
    return ''
  }, [isLoaded])

  const setNumber = useCallback(
    (number: string) => {
      if (instanceRef.current && isLoaded && number) {
        try {
          instanceRef.current.setNumber(number)
        } catch {
          // Ignore errors
        }
      }
    },
    [isLoaded],
  )

  return useMemo(
    () => ({
      isLoaded,
      validateNumber,
      getNumber,
      setNumber,
      instance: instanceRef.current,
    }),
    [isLoaded, validateNumber, getNumber, setNumber],
  )
}
